// Admin v2 — Feliratkozók: a weboldal indulási listája és a nyereményjáték nevezései (Firestore `subscribers`).
// Írni az email-service (Cloud Run) ír; az admin olvas, exportál a sorsoláshoz, és törölheti a teszt-feliratkozókat.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { fmtInt, toDate } from '../data';
import { Badge, Button, Card, Empty, Input, PageHeader, Pagination, Select, Spinner, StatCard, Tabs } from '../ui';

const PAGE_SIZE = 25;

interface Sub {
  id: string;
  email: string;
  name: string;
  confirmed: boolean;
  subscribedAt: Date | null;
  source: string;
  lang: string;
  consent: boolean;
  city: string;
  giveaway: boolean;
  gwEnteredAt: Date | null;
  gwConfirmedAt: Date | null;
  gwValid: boolean | null;
  gwLate: boolean;
}

const fromDoc = (id: string, v: Record<string, unknown>): Sub => ({
  id,
  email: String(v.email ?? id),
  name: String(v.giveawayName ?? ''),
  confirmed: Boolean(v.confirmed),
  subscribedAt: toDate(v.subscribedAt),
  source: String(v.source ?? ''),
  lang: String(v.lang ?? 'hu'),
  consent: Boolean(v.consent),
  city: String(v.city ?? ''),
  giveaway: Boolean(v.giveawayAccepted),
  gwEnteredAt: toDate(v.giveawayEnteredAt),
  gwConfirmedAt: toDate(v.giveawayConfirmedAt),
  gwValid: typeof v.giveawayValid === 'boolean' ? v.giveawayValid : null,
  gwLate: Boolean(v.giveawayLate),
});

const dt = (d: Date | null) => (d ? d.toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

/** Nyereményjáték-állapot: érvényes (sorsolható) / megerősítésre vár / késve érkezett. */
function gwState(s: Sub): { label: string; tone: 'green' | 'gold' | 'red' | 'gray' } {
  if (!s.giveaway) return { label: '—', tone: 'gray' };
  if (s.gwLate || s.gwValid === false) return { label: 'Határidő után', tone: 'red' };
  if (s.gwConfirmedAt || s.gwValid) return { label: 'Érvényes nevezés', tone: 'green' };
  return { label: 'Megerősítésre vár', tone: 'gold' };
}

type Tab = 'all' | 'giveaway' | 'launch';
type Filter = 'all' | 'confirmed' | 'pending' | 'valid';

function csv(rows: Sub[]): string {
  const esc = (x: string) => `"${x.replace(/"/g, '""')}"`;
  const head = ['Név', 'E-mail', 'Megerősítve', 'Nyereményjáték', 'Nevezés állapota', 'Nevezés ideje', 'Megerősítés ideje', 'Feliratkozás', 'Nyelv', 'Forrás', 'Város'];
  const lines = rows.map((s) => [
    s.name, s.email, s.confirmed ? 'igen' : 'nem', s.giveaway ? 'igen' : 'nem', gwState(s).label,
    dt(s.gwEnteredAt), dt(s.gwConfirmedAt), dt(s.subscribedAt), s.lang, s.source, s.city,
  ].map((x) => esc(String(x))).join(';'));
  return '﻿' + [head.map(esc).join(';'), ...lines].join('\r\n'); // BOM + pontosvessző: Excel magyar beállítással is jól nyitja
}

export default function SubscribersPage() {
  const [subs, setSubs] = useState<Sub[] | null>(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => onSnapshot(collection(db, 'subscribers'),
    (s) => setSubs(s.docs.map((d) => fromDoc(d.id, d.data())).sort((a, b) => (b.subscribedAt?.getTime() ?? 0) - (a.subscribedAt?.getTime() ?? 0))),
    (e) => { setErr(e.message); setSubs([]); }), []);

  const stats = useMemo(() => {
    const all = subs ?? [];
    const gw = all.filter((s) => s.giveaway);
    return {
      total: all.length,
      confirmed: all.filter((s) => s.confirmed).length,
      gwValid: gw.filter((s) => gwState(s).tone === 'green').length,
      gwPending: gw.filter((s) => gwState(s).tone === 'gold').length,
      gwLate: gw.filter((s) => gwState(s).tone === 'red').length,
    };
  }, [subs]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (subs ?? []).filter((s) =>
      (tab === 'all' || (tab === 'giveaway' ? s.giveaway : !s.giveaway))
      && (filter === 'all'
        || (filter === 'confirmed' && s.confirmed)
        || (filter === 'pending' && !s.confirmed && !s.gwConfirmedAt)
        || (filter === 'valid' && gwState(s).tone === 'green'))
      && (!needle || `${s.email} ${s.name} ${s.city}`.toLowerCase().includes(needle)));
  }, [subs, tab, filter, q]);

  // szűrés változásakor vissza az első oldalra; törlés után ne maradjunk üres oldalon
  useEffect(() => { setPage(1); }, [tab, filter, q]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const paged = useMemo(() => shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [shown, page]);
  const goPage = (p: number) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const download = () => {
    const blob = new Blob([csv(shown)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `dartiqon-feliratkozok-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const remove = async (s: Sub) => {
    if (!window.confirm(`Véglegesen törlöd ezt a feliratkozót?\n\n${s.name ? `${s.name} — ` : ''}${s.email}\n\nEz nem vonható vissza.`)) return;
    setBusy(s.id);
    try { await deleteDoc(doc(db, 'subscribers', s.id)); } catch (e) { window.alert(e instanceof Error ? e.message : 'A törlés nem sikerült.'); } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader
        title="Feliratkozók"
        sub="A weboldal indulási listája és a nyereményjáték nevezései — élőben."
        right={<Button kind="ghost" icon="DownloadSimple" onClick={download} disabled={!shown.length}>CSV export ({shown.length})</Button>}
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard icon="Envelope" label="Összes feliratkozó" value={fmtInt(stats.total)} />
        <StatCard icon="CheckCircle" tone="green" label="Megerősített e-mail" value={fmtInt(stats.confirmed)} />
        <StatCard icon="Trophy" tone="gold" label="Érvényes nevezés (sorsolható)" value={fmtInt(stats.gwValid)} />
        <StatCard icon="Clock" tone="purple" label="Nevezés megerősítésre vár / késve" value={`${fmtInt(stats.gwPending)} / ${fmtInt(stats.gwLate)}`} />
      </div>

      <Tabs value={tab} onChange={setTab} options={[
        { value: 'all', label: 'Mind' },
        { value: 'giveaway', label: 'Nyereményjáték' },
        { value: 'launch', label: 'Csak indulási lista' },
      ]} />

      <Card className="mb-5 !p-3 sm:!p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <Input icon="MagnifyingGlass" placeholder="Keresés: név, e-mail, város…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={filter} onChange={setFilter} options={[
            { value: 'all', label: 'Minden állapot' },
            { value: 'confirmed', label: 'Megerősített' },
            { value: 'pending', label: 'Megerősítésre vár' },
            { value: 'valid', label: 'Érvényes nevezés' },
          ]} />
        </div>
      </Card>

      {err && <Card className="mb-4"><span className="text-[#ef4444] text-[13px]">Nem sikerült betölteni: {err}</span></Card>}

      {subs === null ? <Spinner /> : shown.length === 0 ? (
        <Card><Empty>Nincs feliratkozó a szűrésnek megfelelően.</Empty></Card>
      ) : (
        <>
          {/* mobil / tablet: kártyák */}
          <div className="lg:hidden flex flex-col gap-3">
            {paged.map((s) => {
              const g = gwState(s);
              const ok = s.confirmed || !!s.gwConfirmedAt;
              return (
                <Card key={s.id} className="!p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white font-semibold text-[14.5px] truncate">{s.name || 'Név nélkül'}</div>
                      <div className="text-[#B8B8C3] text-[13px] break-all">{s.email}</div>
                    </div>
                    <span className="text-[11.5px] uppercase text-[#8a8f99] shrink-0 mt-0.5">{s.lang}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge tone={ok ? 'green' : 'gold'}>{ok ? 'E-mail megerősítve' : 'E-mail még nincs megerősítve'}</Badge>
                    {s.giveaway && <Badge tone={g.tone}>{g.label}</Badge>}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
                    <span className="text-[#8a8f99]">Feliratkozás</span>
                    <span className="text-[#B8B8C3] text-right">{dt(s.subscribedAt)}</span>
                    {s.giveaway && <>
                      <span className="text-[#8a8f99]">Nevezés</span>
                      <span className="text-[#B8B8C3] text-right">{dt(s.gwEnteredAt)}</span>
                    </>}
                    {s.city && <>
                      <span className="text-[#8a8f99]">Város</span>
                      <span className="text-[#B8B8C3] text-right">{s.city}</span>
                    </>}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button kind="danger" icon="Trash" disabled={busy === s.id} onClick={() => void remove(s)}>Törlés</Button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* asztali: táblázat */}
          <Card pad={false} className="hidden lg:block overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[#8a8f99] text-[12px] border-b border-[#31383D]">
                  <th className="px-5 py-3 font-medium">Név</th>
                  <th className="px-3 py-3 font-medium">E-mail</th>
                  <th className="px-3 py-3 font-medium">E-mail megerősítve</th>
                  <th className="px-3 py-3 font-medium">Nyereményjáték</th>
                  <th className="px-3 py-3 font-medium">Feliratkozás</th>
                  <th className="px-3 py-3 font-medium">Nyelv</th>
                  <th className="px-5 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {paged.map((s) => {
                  const g = gwState(s);
                  return (
                    <tr key={s.id} className="border-b border-[#31383D]/60 hover:bg-white/[0.03]">
                      <td className="px-5 py-3 text-white">{s.name || <span className="text-[#8a8f99]">—</span>}</td>
                      <td className="px-3 py-3 text-white break-all">{s.email}</td>
                      <td className="px-3 py-3"><Badge tone={s.confirmed || s.gwConfirmedAt ? 'green' : 'gold'}>{s.confirmed || s.gwConfirmedAt ? 'Igen' : 'Még nem'}</Badge></td>
                      <td className="px-3 py-3">
                        {s.giveaway ? (
                          <span className="flex flex-col gap-1">
                            <Badge tone={g.tone}>{g.label}</Badge>
                            <span className="text-[11.5px] text-[#8a8f99]">nevezett: {dt(s.gwEnteredAt)}</span>
                          </span>
                        ) : <span className="text-[#8a8f99]">—</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-[#B8B8C3]">{dt(s.subscribedAt)}</td>
                      <td className="px-3 py-3 uppercase text-[#B8B8C3]">{s.lang}</td>
                      <td className="px-5 py-3 text-right">
                        <Button kind="danger" icon="Trash" disabled={busy === s.id} onClick={() => void remove(s)}>Törlés</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
          <Pagination page={page} total={shown.length} pageSize={PAGE_SIZE} onChange={goPage} />
        </>
      )}
    </div>
  );
}
